/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';

import EditIcon from '../../../../Assets/images/svg/dar_pencil_gray_opacity.svg';

const resolveIconSrc = (icon) => {
  if (typeof icon === 'string') return icon;
  if (icon && typeof icon === 'object' && typeof icon.default === 'string') return icon.default;
  return '';
};
const handleIconError = (event) => {
  event.currentTarget.style.display = 'none';
};

const isProjectOfferingLabel = (label) => String(label || '').trim()
  .toLowerCase()
  .includes('offering');

const splitOfferingLines = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);
const isEffortLoggedResource = (label) => String(label || '').trim()
  .startsWith('Effort logged');
const isDatedEffortLoggedResource = (label) => /^Effort logged\s*\(On\s+.+\)$/i.test(String(label || '').trim());
const isOverallEffortLoggedResource = (label) => /^Effort logged\s*\(Overall\)$/i.test(String(label || '').trim());
const isAllocationResource = (label) => {
  const normalizedLabel = String(label || '').trim();
  return normalizedLabel === 'Staffed'
    || normalizedLabel.startsWith('Idle time in hrs');
};
const getEffortLoggedColorClass = (label, value) => {
  if (!isDatedEffortLoggedResource(label) && !isOverallEffortLoggedResource(label)) return '';

  const numericValue = Number.parseFloat(String(value || '').replace('%', '')
    .trim());
  if (!Number.isFinite(numericValue)) return '';

  if (numericValue < 80) return 'red';
  if (numericValue >= 80 && numericValue <= 90) return 'yellow';
  return 'green';
};
const isJiraProgressLabel = (label) => String(label || '').trim()
  .toLowerCase()
  .includes('jira');
const isInProgressStatus = (status) => String(status || '').trim()
  .toLowerCase() === 'in progress';

const parseProgressAsOnDateFromLabel = (label) => {
  const progressDateRegex = /\(\s*on\s+(.+?)\s*\)$/i;
  const match = progressDateRegex.exec(String(label || '').trim());
  if (!match?.[1]) return null;

  const rawDate = match[1].trim();
  const parsedDate = new Date(rawDate);
  if (Number.isFinite(parsedDate.getTime())) {
    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate;
  }

  const ddMmmYyyyRegex = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/;
  const ddMmmYyyyMatch = ddMmmYyyyRegex.exec(rawDate);
  if (!ddMmmYyyyMatch) return null;

  const [, dayStr, monthStr, yearStr] = ddMmmYyyyMatch;
  const monthMap = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };
  const month = monthMap[monthStr.toLowerCase()];
  if (month === undefined) return null;

  const day = Number(dayStr);
  const year = Number(yearStr);
  const resolvedDate = new Date(year, month, day);
  if (!Number.isFinite(resolvedDate.getTime())) return null;
  resolvedDate.setHours(0, 0, 0, 0);
  return resolvedDate;
};

const shouldHighlightProgressDate = (status, label) => {
  if (!isInProgressStatus(status)) return false;

  const asOnDate = parseProgressAsOnDateFromLabel(label);
  if (!asOnDate) return false;

  const cutoffDate = new Date();
  cutoffDate.setHours(0, 0, 0, 0);
  cutoffDate.setDate(cutoffDate.getDate() - 10);

  return asOnDate < cutoffDate;
};

const renderProgressLabel = (label, projectStatus) => {
  const labelText = String(label || '');
  if (!shouldHighlightProgressDate(projectStatus, labelText)) {
    return <>{labelText}</>;
  }

  const highlightedLabelRegex = /^(.*\(\s*On\s+)(.+?)(\s*\).*)$/i;
  const match = highlightedLabelRegex.exec(labelText);
  if (!match) return <>{labelText}</>;

  const [, prefix, dateText, suffix] = match;
  return (
    <>
      {prefix}
      <span className="red">{dateText}</span>
      {suffix}
    </>
  );
};

const LeftMainCard = ({
  basicDetails,
  resources,
  progress,
  showProgressAsNA,
  onEditProgress,
  canViewEffortLoggedResources,
  canViewAllocationResources,
  canViewProgress,
  renderProgressIcon,
  projectStatus,
  projectType,
}) => {
  const maskedResources = (resources || []).map((item) => {
    const label = item?.label;
    if (isEffortLoggedResource(label)) {
      return canViewEffortLoggedResources ? item : { ...item, value: '0' };
    }
    if (isAllocationResource(label)) {
      return canViewAllocationResources ? item : { ...item, value: '0' };
    }
    return item;
  });
  const maskedProgress = (progress || []).map((item) => {
    if (showProgressAsNA) {
      const baseProgressItem = canViewProgress ? item : { ...item, value: '0' };
      if (isJiraProgressLabel(item?.label)) return baseProgressItem;
      return { ...baseProgressItem, value: 'NA' };
    }
    return canViewProgress ? item : { ...item, value: '0' };
  });

  return (
    <div className="card left-main-card">
      <div className="section">
        <div className="section-header">
          <h3>Basic details</h3>
          <span className="divider" />
        </div>
        <div className="section-content">
          {(basicDetails || []).map((item) => (
            <div
              key={`${item.label}-${item.value}`}
              className={[
                'detail-row',
                'detail-row-basic',
                isProjectOfferingLabel(item.label) ? 'detail-row-offering' : '',
              ].join(' ')}
            >
              <div className="detail-left">
                <span className="icon">
                  {typeof item.icon === 'string' && item.icon.includes('.svg')
                    ? <img src={resolveIconSrc(item.icon)} alt="" onError={handleIconError} />
                    : item.icon}
                </span>
                <span className="detail-label">{item.label}</span>
              </div>
              {isProjectOfferingLabel(item.label) ? (
                <div className="basic-value-multiline offering-value">
                  {splitOfferingLines(item.value).map((offering, index) => (
                    <div key={`${offering}-${index}`} className="basic-detail-offering">
                      <span key={`${offering}-${index}`} className="detail-text-offering">{offering}</span>
                      <div className="detail-value-tooltip-offering">{offering}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="basic-detail">
                  <span className="detail-text">{item.value}</span>
                  <div className="detail-value-tooltip">{item.value}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <h3>Resources</h3>
          <span className="divider" />
        </div>
        <div className="section-content">
          {maskedResources.map((item) => (
            <div key={`${item.label}-${item.value}`} className="detail-row">
              <div className="detail-left">
                <span className="icon">
                  {typeof item.icon === 'string' && item.icon.includes('.svg')
                    ? <img src={resolveIconSrc(item.icon)} alt="" onError={handleIconError} />
                    : item.icon}
                </span>
                <span className="detail-label">{item.label}</span>
              </div>
              <span
                className={[
                  'detail-value',
                  getEffortLoggedColorClass(item.label, item.value) || (item.red ? 'red' : ''),
                ].join(' ').trim()}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <h3>Progress %</h3>
          <span className="divider" />
          {onEditProgress && projectType === 'fp' ? (
            <button className="edit-icon" aria-label="Edit" onClick={onEditProgress}>
              <img src={resolveIconSrc(EditIcon)} alt="" onError={handleIconError} />
            </button>
          ) : null}
        </div>
        <div className="section-content">
          {maskedProgress.map((item) => (
            <div key={`${item.label}-${item.value}`} className="detail-row">
              <div className="detail-left">
                <span className="icon">{renderProgressIcon(item)}</span>
                <span className="detail-label">{renderProgressLabel(item.label, projectStatus)}</span>
              </div>
              <span className="detail-value">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeftMainCard;

LeftMainCard.propTypes = {
  basicDetails: PropTypes.arrayOf(PropTypes.object),
  resources: PropTypes.arrayOf(PropTypes.object),
  progress: PropTypes.arrayOf(PropTypes.object),
  showProgressAsNA: PropTypes.bool,
  onEditProgress: PropTypes.func,
  canViewEffortLoggedResources: PropTypes.bool,
  canViewAllocationResources: PropTypes.bool,
  canViewProgress: PropTypes.bool,
  renderProgressIcon: PropTypes.func.isRequired,
  projectStatus: PropTypes.string,
  projectType: PropTypes.string,
};

LeftMainCard.defaultProps = {
  basicDetails: [],
  resources: [],
  progress: [],
  showProgressAsNA: false,
  onEditProgress: null,
  canViewEffortLoggedResources: false,
  canViewAllocationResources: false,
  canViewProgress: false,
  projectStatus: '',
  projectType: '',
};
